import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { MOCK_WORKOUTS, MOCK_LOGS, MOCK_TRAINING_BLOCKS } from '../utils/mockData';
import Icon from '../components/shared/Icon';
import './WorkoutsPage.css';

function formatWU(wu) {
  if (!wu) return '—';
  return wu >= 1000 ? `${(wu / 1000).toFixed(1)}k WU` : `${wu} WU`;
}

function formatDate(dateStr) {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export default function WorkoutsPage() {
  const navigate = useNavigate();
  const today = new Date().toISOString().slice(0, 10);

  const [expandedBlock, setExpandedBlock] = useState('tb-001');

  const completedIds = new Set(MOCK_LOGS.filter((l) => l.completed).map((l) => l.workoutId));

  const todayWorkout = MOCK_WORKOUTS.find((w) => w.scheduledDate === today);
  const todayDone = todayWorkout ? completedIds.has(todayWorkout.id) : false;

  const activeBlock = MOCK_TRAINING_BLOCKS.find(
    (b) => b.startDate <= today && b.endDate >= today
  );

  const archive = MOCK_WORKOUTS
    .filter((w) => w.scheduledDate < today)
    .sort((a, b) => b.scheduledDate.localeCompare(a.scheduledDate));

  return (
    <div className="workouts-page">

      {/* ── Today Hero ────────────────────────────── */}
      <section className="workouts-page__section">
        <div className="workouts-page__section-head">
          <h2 className="workouts-page__section-title">Today</h2>
          <span className="workouts-page__section-date">{formatDate(today)}</span>
        </div>

        {todayWorkout ? (
          <Link
            to={`/app/workouts/${todayWorkout.id}`}
            className={`workouts-today-card ${todayDone ? 'workouts-today-card--done' : 'workouts-today-card--pending'}`}
          >
            <div className="workouts-today-card__main">
              <div className="workouts-today-card__info">
                <h3 className="workouts-today-card__name">{todayWorkout.name}</h3>
                {todayWorkout.description && (
                  <p className="workouts-today-card__desc">{todayWorkout.description}</p>
                )}
              </div>
              <span className={`workouts-today-card__badge ${todayDone ? 'workouts-today-card__badge--done' : 'workouts-today-card__badge--go'}`}>
                {todayDone ? <><Icon name="check" size={11} /> Done</> : 'Go →'}
              </span>
            </div>
            <div className="workouts-today-card__meta">
              <span><Icon name="clock" size={12} /> {todayWorkout.duration} min</span>
              <span><Icon name="zap" size={12} /> {formatWU(todayWorkout.workOutputTarget)}</span>
              <span><Icon name="list" size={12} /> {todayWorkout.exercises.length} exercises</span>
            </div>
            {todayWorkout.tags?.length > 0 && (
              <div className="workouts-today-card__tags">
                {todayWorkout.tags.map((t) => (
                  <span key={t} className="workouts-today-card__tag">{t}</span>
                ))}
              </div>
            )}
          </Link>
        ) : (
          <div className="workouts-rest-card">
            <span className="workouts-rest-card__icon">☀</span>
            <div>
              <p className="workouts-rest-card__title">Rest Day</p>
              <p className="workouts-rest-card__sub">No workout scheduled today</p>
            </div>
          </div>
        )}
      </section>

      {/* ── Training Block ────────────────────────── */}
      {activeBlock && (
        <section className="workouts-page__section">
          <div className="workouts-page__section-head">
            <h2 className="workouts-page__section-title">Training Block</h2>
          </div>

          <div className="workouts-block">
            <button
              className="workouts-block__header"
              onClick={() => setExpandedBlock(expandedBlock === activeBlock.id ? null : activeBlock.id)}
              type="button"
            >
              <div className="workouts-block__header-left">
                <span className="workouts-block__phase-badge">{activeBlock.phase}</span>
                <div>
                  <p className="workouts-block__name">{activeBlock.name}</p>
                  <p className="workouts-block__weeks">{activeBlock.weekCount} weeks · {formatDate(activeBlock.startDate)} – {formatDate(activeBlock.endDate)}</p>
                </div>
              </div>
              <Icon name={expandedBlock === activeBlock.id ? 'chevron-up' : 'chevron-down'} size={16} />
            </button>

            {expandedBlock === activeBlock.id && (
              <div className="workouts-block__body">
                <p className="workouts-block__desc">{activeBlock.description}</p>
                <div className="workouts-block__list">
                  {activeBlock.workoutIds.map((wid) => {
                    const w = MOCK_WORKOUTS.find((x) => x.id === wid);
                    if (!w) return null;
                    const done = completedIds.has(w.id);
                    const isToday = w.scheduledDate === today;
                    return (
                      <Link
                        key={wid}
                        to={`/app/workouts/${w.id}`}
                        className={`workouts-block__item ${done ? 'workouts-block__item--done' : ''} ${isToday ? 'workouts-block__item--today' : ''}`}
                      >
                        <span className={`workouts-block__dot ${done ? 'workouts-block__dot--done' : isToday ? 'workouts-block__dot--today' : ''}`} />
                        <span className="workouts-block__item-name">{w.name}</span>
                        <span className="workouts-block__item-date">{formatDate(w.scheduledDate)}</span>
                        {done && <Icon name="check" size={12} className="workouts-block__check" />}
                      </Link>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* ── Archive ───────────────────────────────── */}
      {archive.length > 0 && (
        <section className="workouts-page__section">
          <div className="workouts-page__section-head">
            <h2 className="workouts-page__section-title">Archive</h2>
            <span className="workouts-page__section-count">{archive.length}</span>
          </div>

          <div className="workouts-archive">
            {archive.map((w) => {
              const done = completedIds.has(w.id);
              return (
                <Link
                  key={w.id}
                  to={`/app/workouts/${w.id}`}
                  className={`workouts-archive__item ${done ? 'workouts-archive__item--done' : ''}`}
                >
                  <span className={`workouts-archive__dot ${done ? 'workouts-archive__dot--done' : ''}`} />
                  <div className="workouts-archive__info">
                    <p className="workouts-archive__name">{w.name}</p>
                    <p className="workouts-archive__meta">{formatDate(w.scheduledDate)} · {w.duration} min · {formatWU(w.workOutputTarget)}</p>
                  </div>
                  <Icon name="chevron-right" size={14} className="workouts-archive__chevron" />
                </Link>
              );
            })}
          </div>
        </section>
      )}

      {/* ── CTA Row ───────────────────────────────── */}
      <div className="workouts-page__cta">
        <button
          className="btn workouts-page__cta-btn workouts-page__cta-btn--build"
          onClick={() => navigate('/app/workout/new')}
          type="button"
        >
          <Icon name="plus" size={16} /> Build Workout
        </button>
        <button className="btn workouts-page__cta-btn workouts-page__cta-btn--ai" disabled type="button">
          <Icon name="streak" size={16} /> AI Workout
        </button>
      </div>

    </div>
  );
}
